import React from "react";
import { useAuth } from "@/lib/AuthContext";
import { useAnnoScolastico } from "@/lib/annoScolastico";

/**
 * Selettore Anno Scolastico Attivo — bianco su blu, da posizionare come PRIMO campo
 * della riga filtri delle tabelle di Segremat Web Pro.
 *
 * Feb 2026: sincronizza bidirezionalmente con il context globale `useAnnoScolastico`
 * (persistito in localStorage `hub_anno_attivo`, condiviso col selettore in alto a destra)
 * E con `annoCorrente` di AuthContext (backend `app_settings.anno_corrente`).
 * Fonte di verità visualizzata: `useAnnoScolastico().anno` (fallback: `annoCorrente`).
 *
 * Props:
 *   range: numero di anni indietro/avanti rispetto ad annoCorrente (default 3 indietro, 1 avanti)
 *   testId: data-testid per il select
 */
export default function AnnoSelector({ range = [3, 1], testId = "anno-attivo-select" }) {
  const { annoCorrente, setAnnoCorrente } = useAuth();
  const { anno: annoGlobal, setAnno: setAnnoGlobal } = useAnnoScolastico();
  const [saving, setSaving] = React.useState(false);
  // Fonte di verità: context globale (preferito) → fallback annoCorrente AuthContext
  const active = annoGlobal ?? annoCorrente;

  // Se cambia il context globale (da altro selettore), sincronizza AuthContext
  React.useEffect(() => {
    if (annoGlobal != null && annoGlobal !== annoCorrente) {
      setAnnoCorrente(annoGlobal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annoGlobal]);

  const anni = [];
  if (active) {
    for (let y = active - range[0]; y <= active + range[1]; y++) anni.push(y);
  }

  const onChange = async (newYear) => {
    // Aggiorna entrambi i sistemi (context globale + AuthContext + backend)
    setAnnoGlobal(newYear);
    setAnnoCorrente(newYear);
    setSaving(true);
    try {
      const { saveAppSettings } = await import("@/lib/api");
      await saveAppSettings({ anno_corrente: newYear });
    } catch (e) {
      // ignora: il cambio locale rimane comunque attivo fino al prossimo refresh
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="anno-attivo-block"
      style={{
        display: "inline-flex", alignItems: "center", gap: 8,
        padding: "5px 12px",
        background: "linear-gradient(90deg, #0d47a1, #1565c0)",
        borderRadius: 4,
        boxShadow: "0 1px 3px rgba(13,71,161,0.3)",
        opacity: saving ? 0.7 : 1,
      }}
      title="Anno Scolastico attivo — sincronizzato con il selettore in alto a destra e con tutte le tabelle">
      <span style={{ color: "white", fontSize: 11, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase" }}>
        A.S.
      </span>
      <select data-testid={testId} value={active || ""} disabled={saving}
        onChange={e => onChange(parseInt(e.target.value, 10))}
        style={{
          padding: "4px 10px", fontSize: 13, fontWeight: 700,
          background: "white", color: "#0d47a1",
          border: "2px solid white", borderRadius: 3,
          letterSpacing: 0.3, cursor: saving ? "wait" : "pointer",
        }}>
        {anni.map(y => (
          <option key={y} value={y}>{y}/{(y + 1) % 100 < 10 ? "0" : ""}{(y + 1) % 100}</option>
        ))}
      </select>
    </div>
  );
}
